import { motion, useReducedMotion } from 'framer-motion';
import { Reveal, RevealGroup, RevealItem } from '../Reveal';
import { ButtonLink, SectionHeading, cx } from '../ui';

const FEATURES = [
  {
    icon: '🗓️',
    title: 'Таймлайн по дням',
    body: 'Каждый день — со временем, местами, переездами и заметками. Перетащили пункт — маршрут перестроился.',
    span: 'md:col-span-2',
    tint: 'from-clay-500/10 to-transparent',
  },
  {
    icon: '🗺️',
    title: 'Живая карта',
    body: 'Все точки, базы и линия маршрута. Цвет дня — цвет базы, так что видно, где вы ночуете и куда едете.',
    span: '',
    tint: 'from-lagoon-500/10 to-transparent',
  },
  {
    icon: '🤖',
    title: 'AI-помощник',
    body: 'Составит маршрут целиком или допишет один день. Проверяет, что дневной план физически выполним.',
    span: '',
    tint: 'from-sun-400/12 to-transparent',
  },
  {
    icon: '📸',
    title: 'Фото по геометкам',
    body: 'Загружаете папку — фотографии сами встают на места по координатам и времени съёмки.',
    span: 'md:col-span-2',
    tint: 'from-clay-400/10 to-transparent',
  },
  {
    icon: '🎫',
    title: 'Билеты и брони',
    body: 'PDF, номера подтверждений, PIN-коды от апартаментов — под рукой и видны только вам.',
    span: '',
    tint: 'from-ink-500/8 to-transparent',
  },
  {
    icon: '🍽️',
    title: 'Где есть',
    body: 'Проверенные места рядом с маршрутом, а не первый попавшийся ресторан с набережной.',
    span: '',
    tint: 'from-sun-500/10 to-transparent',
  },
  {
    icon: '📴',
    title: 'Работает без сети',
    body: 'Открытое путешествие остаётся на телефоне: маршрут, брони, документы и карта открываются по той же ссылке в самолёте и без роуминга.',
    span: '',
    tint: 'from-lagoon-400/10 to-transparent',
  },
  {
    icon: '🧬',
    title: 'Копия чужого маршрута',
    body: 'Понравилось чужое путешествие — заберите его себе как черновик и меняйте под себя.',
    span: '',
    tint: 'from-clay-500/10 to-transparent',
  },
  {
    icon: '🔥',
    title: 'Горячие туры и билеты',
    body: 'Свежие туры и дешёвые авиабилеты из проверенных источников — по вашему городу и датам. Выбранное предложение сразу становится поездкой с бронями.',
    span: 'md:col-span-2',
    tint: 'from-sun-400/12 to-transparent',
    link: { to: '/deals', label: 'Подобрать предложения' },
  },
];

/** Bento grid. Cards lift and their tint blooms on hover. */
export function Features() {
  const reduced = useReducedMotion();

  return (
    <section className="relative py-24 md:py-32">
      <div className="container-page">
        <Reveal>
          <SectionHeading
            eyebrow="Что внутри"
            title="Всё о поездке — в одном месте"
            description="От первой идеи до фотоальбома, который не стыдно показать. И с контролем над тем, что из этого видят остальные."
          />
        </Reveal>

        <RevealGroup className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature) => (
            <RevealItem key={feature.title} className={cx(feature.span)}>
              <motion.article
                whileHover={reduced ? undefined : { y: -6 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="surface-card group relative h-full overflow-hidden p-6"
              >
                <div
                  className={cx(
                    'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-500 group-hover:opacity-100',
                    feature.tint,
                  )}
                />
                <div className="relative">
                  <motion.div
                    whileHover={reduced ? undefined : { rotate: -8, scale: 1.12 }}
                    transition={{ duration: 0.3 }}
                    className="mb-4 grid size-11 place-items-center rounded-xl bg-[var(--surface-sunken)] text-xl"
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="text-lg font-semibold tracking-tight">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">
                    {feature.body}
                  </p>
                  {feature.link ? (
                    <ButtonLink to={feature.link.to} variant="outline" size="sm" className="mt-4">
                      {feature.link.label} →
                    </ButtonLink>
                  ) : null}
                </div>
              </motion.article>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}
